import React, { useState, useContext, useMemo, useEffect } from 'react';
import { compose } from 'recompose';
import { useNavigate } from 'react-router';
import {
    Table,
    Tabs,
    Tab,
    Typography,
    TableHead,
    TableFooter,
    TablePagination,
    TableBody,
    TableRow,
    TableCell,
    Paper,
    Button,
    TextField,
    Checkbox,
} from '@material-ui/core';
import { Autocomplete, Skeleton } from '@material-ui/lab';
import _ from 'lodash';
import { css } from '@emotion/react';
import withAdminRights from '../../Auth/withAdminRights';
import * as ROUTES from '../../../constants/routes';
import { UserContext } from '../../App';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { QUERY_USERS_BY_ROLE } from './queries';
import { COLUMNS, SEARCHABLE, TABS } from './constants';
import { UPDATE_CLIENT_BY_ID } from './mutations';
import { AIRPORT_CODES, AIRPORT_CODES_BY_ZIP } from '@/constants/airportCodes';
import { OnwardChip } from '@/styles/blocks';
import { setImpersonatedClientId } from '@/utilities/impersonation';

const AdminUsers = () => {
    const [tab, setTab] = useState(TABS[0]);
    const [page, setPage] = useState(0);
    const [searchInput, setSearchInput] = useState('');

    const { sidebarCollapsed, setClientId } = useContext(UserContext);
    const navigate = useNavigate();

    const { loading, error, data } = useQuery(QUERY_USERS_BY_ROLE);

    const [updateClient, { loading: updateLoading }] = useMutation(UPDATE_CLIENT_BY_ID);
    const [selectedAirports, setSelectedAirports] = useState([]);

    const filteredUsers = useMemo(() => {
        const tabUsers = data?.[tab.data] || [];

        let tabUsersWithAirportCodes;

        if (tab.data === 'carriers') {
            tabUsersWithAirportCodes = tabUsers
                .map((tabUser) => {
                    const airportCodes = [];
                    tabUser.locations?.forEach((location) => {
                        const airportCode = AIRPORT_CODES_BY_ZIP[location.business_zip];
                        if (airportCode && !airportCodes.includes(airportCode)) {
                            airportCodes.push(airportCode);
                        }
                    });
                    return {
                        ...tabUser,
                        airportCodes,
                    };
                })
                .filter((tabUser) => {
                    if (selectedAirports.length === 0) return true;
                    return tabUser.airportCodes.some((code) => selectedAirports.includes(code));
                });
        } else {
            tabUsersWithAirportCodes = tabUsers;
        }

        if (searchInput && searchInput.length) {
            return tabUsersWithAirportCodes.filter((tabUser) =>
                SEARCHABLE.some((searchField) => {
                    const tabUserField = _.get(tabUser, searchField);
                    if (tabUserField && tabUserField.length) {
                        const fieldLower = tabUserField.toLowerCase();
                        const searchLower = searchInput.toLowerCase();
                        return fieldLower.includes(searchLower) || searchLower.includes(fieldLower);
                    }
                    return false;
                })
            );
        }

        return tabUsersWithAirportCodes;
    }, [data, tab, searchInput, selectedAirports]);

    const airportOptions = useMemo(() => {
        return AIRPORT_CODES.map((airport) => {
            return {
                label: airport,
                value: airport,
                selected: selectedAirports.includes(airport),
            };
        });
    }, [AIRPORT_CODES, selectedAirports]);

    const changeTab = (...[, newTab]) => {
        setTab(TABS[newTab]);
        setPage(0);
    };

    const logInAsUser = async (entity) => {
        const { user_id, username, roles } = entity.user;

        if (roles?.['ONWARD_ADMIN']) {
            alert('Logging in as another admin is not allowed');
            return;
        }

        setImpersonatedClientId(user_id);
        setClientId(user_id);
        navigate('/account');
    };

    const goToUser = (entity) => {
        if (entity.user.roles.SHIPPER) {
            navigate(`/admin/shipper/${entity.client_id}`);
        } else if (entity.user.roles.CARRIER || entity.user.roles.CONTRACTOR) {
            navigate(`/admin/carrier/${entity.client_id}`);
        }
    };

    const colFilter = (column) => !column.tabs || column.tabs.includes(tab.header);

    return (
        <div
            style={{ margin: '16px' }}
            className={`${sidebarCollapsed && 'collapse-margin'} sidenav-margin-responsive`}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography style={{ fontWeight: 600 }} variant="h4">
                    Super Admin
                </Typography>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    {tab.data === 'carriers' && (
                        <Autocomplete
                            size="small"
                            css={css`
                                margin-right: 15px;
                                width: 350px;
                            `}
                            multiple
                            disableCloseOnSelect
                            value={airportOptions.filter((airport) => airport.selected)}
                            options={airportOptions}
                            getOptionSelected={(option, value) => {
                                return option.value === value.value;
                            }}
                            getOptionLabel={(option) => option.label}
                            renderTags={(options, getTagProps) => {
                                return options.map((option, index) => (
                                    <OnwardChip
                                        key={option.value}
                                        label={option.label}
                                        {...getTagProps({ index })}
                                        {...(option.disabled ? { onDelete: null } : {})}
                                        css={css`
                                            color: ${option.disabled ? '#8d8d8d' : 'black'};
                                        `}
                                    />
                                ));
                            }}
                            renderOption={(option, { selected }) => {
                                return (
                                    <li key={option.value}>
                                        <Checkbox color="primary" checked={selected} />
                                        {option.label}
                                    </li>
                                );
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    variant="outlined"
                                    label="Airport Codes"
                                    onKeyDown={(e) => {
                                        if (['Backspace'].includes(e.key)) {
                                            e.stopPropagation();
                                        }
                                    }}
                                />
                            )}
                            onChange={(e, value = []) => {
                                const _selectedAirports = value.map((option) => option.label);
                                setSelectedAirports(_selectedAirports);
                            }}
                        />
                    )}

                    <TextField
                        size="small"
                        variant="outlined"
                        color="primary"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        placeholder="Search..."
                    />
                </div>
            </div>

            <Tabs textColor="primary" indicatorColor="primary" value={tab.index} onChange={changeTab}>
                {TABS.map((tab) => (
                    <Tab label={tab.header} key={tab.header} />
                ))}
            </Tabs>

            <Paper>
                <Table size="medium">
                    <TableHead>
                        <TableRow>
                            {COLUMNS.filter(colFilter).map((column) => (
                                <TableCell key={column.label} align="center">
                                    {column.label}
                                </TableCell>
                            ))}
                            <TableCell />
                            {tab.header !== 'Teammates' && <TableCell />}
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {loading
                            ? [...Array(5)].map((...[, i]) => (
                                  <TableRow key={i}>
                                      <TableCell colSpan={COLUMNS.filter(colFilter).length + 1}>
                                          <Skeleton variant="text" />
                                      </TableCell>
                                  </TableRow>
                              ))
                            : filteredUsers.slice(page * 10, page * 10 + 10).map((entity) => (
                                  <TableRow key={entity.user.user_id}>
                                      {COLUMNS.filter(colFilter).map((column) => (
                                          <TableCell align="center" key={`${entity.user.user_id}-${column.label}`}>
                                              {column.value(entity, loading || updateLoading, {
                                                  updateClient: (client_id, update = {}) =>
                                                      updateClient({ variables: { client_id, update } }),
                                              })}
                                          </TableCell>
                                      ))}
                                      <TableCell align="center">
                                          <Button
                                              variant="contained"
                                              style={{ color: '#eee' }}
                                              color="primary"
                                              onClick={() => logInAsUser(entity)}
                                          >
                                              Log&nbsp;In
                                          </Button>
                                      </TableCell>
                                      {tab.header !== 'Teammates' && (
                                          <TableCell align="center">
                                              <Button
                                                  variant="contained"
                                                  style={{ color: '#eee' }}
                                                  color="primary"
                                                  onClick={() => goToUser(entity)}
                                              >
                                                  Details
                                              </Button>
                                          </TableCell>
                                      )}
                                  </TableRow>
                              ))}
                    </TableBody>

                    <TableFooter>
                        <TableRow>
                            <TablePagination
                                count={filteredUsers.length}
                                page={page}
                                rowsPerPage={10}
                                rowsPerPageOptions={[]}
                                onPageChange={(...[, newPage]) => setPage(newPage)}
                                style={{ borderBottom: 'none' }}
                            />
                        </TableRow>
                    </TableFooter>
                </Table>
            </Paper>
        </div>
    );
};

export default React.memo(compose(withAdminRights)(AdminUsers));
